import { SecondaryPageLink } from '@/PageManager'
import { fetchEvent } from '@/services/client.service'
import { toNote } from '@/lib/link'
import { cn } from '@/lib/utils'
import type { AddressPointer } from '@nostr/tools/nip19'
import type { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'
import Username from '@/components/Username'
import { BadgeSlot, parseBadgePairs } from './badge-utils'

/**
 * NIP-58 profile badges: replaceable kind 10008 (and deprecated addressable
 * kind 30008 with `d` == "profile_badges"). Ordered `a`/`e` pairs of badge
 * definition + award, plus optional `a` refs to badge sets.
 */
export default function ProfileBadges({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const { pairs, setRefs } = useMemo(() => parseBadgePairs(event.tags), [event])

  return (
    <div className={cn('space-y-3', className)}>
      <div className="text-sm font-medium text-muted-foreground">{t('Badges')}</div>
      {pairs.length === 0 && setRefs.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t('No badges to display.')}</div>
      ) : (
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {pairs.map((pair, i) => (
            <BadgeSlot
              key={`${pair.definition.kind}:${pair.definition.pubkey}:${pair.definition.identifier}:${pair.award?.id ?? i}`}
              definition={pair.definition}
              award={pair.award}
            />
          ))}
        </div>
      )}
      {setRefs.map((ptr) => (
        <BadgeSetSection
          key={`${ptr.pubkey}:${ptr.identifier}`}
          pointer={ptr}
          owner={event.pubkey}
        />
      ))}
    </div>
  )
}

/** Inline expansion of a badge set referenced from a profile-badges event. */
export function BadgeSetSection({ pointer, owner }: { pointer: AddressPointer; owner?: string }) {
  const { t } = useTranslation()
  const { value: setEvent } = useAsync(
    async () => fetchEvent(pointer),
    [pointer.kind, pointer.pubkey, pointer.identifier]
  )
  const { pairs } = useMemo(() => parseBadgePairs(setEvent?.tags ?? []), [setEvent])
  const d = setEvent?.tags.find((tag) => tag[0] === 'd')?.[1]
  const title = setEvent?.tags.find((tag) => tag[0] === 'title')?.[1] || d

  if (!setEvent) {
    return (
      <div className="text-sm text-muted-foreground">
        <SecondaryPageLink
          to={toNote(pointer)}
          onClick={(e) => e.stopPropagation()}
          className="font-mono text-xs hover:underline"
        >
          {pointer.identifier || t('Badge set')}
        </SecondaryPageLink>
      </div>
    )
  }

  return (
    <div className="space-y-1 rounded-lg border p-2">
      <SecondaryPageLink
        to={toNote(setEvent)}
        onClick={(e) => e.stopPropagation()}
        className="block truncate text-sm font-semibold hover:underline"
      >
        {title || t('Badge set')}
      </SecondaryPageLink>
      {owner && setEvent.pubkey !== owner && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>{t('by')}</span>
          <Username user={setEvent.pubkey} className="font-semibold" />
        </div>
      )}
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {pairs.map((pair, i) => (
          <BadgeSlot
            key={`${pair.definition.kind}:${pair.definition.pubkey}:${pair.definition.identifier}:${pair.award?.id ?? i}`}
            definition={pair.definition}
            award={pair.award}
            size="sm"
          />
        ))}
      </div>
    </div>
  )
}
